/**
 * Supabase client singleton (frontend, anon key only).
 *
 * Used ONLY for Realtime subscriptions — never for direct table reads or
 * writes. Authoritative reads/writes go through the FastAPI backend so
 * the existing JWT-based RBAC stays in control.
 *
 * When realtime fires a `postgres_changes` event we treat it as a
 * low-latency *signal* to call our backend's reload endpoints; we don't
 * trust the payload alone for cross-org isolation.
 *
 * RLS COMPATIBILITY
 * -----------------
 * Once the operational tables are RLS-protected (alembic 0006), the
 * Realtime connection must carry a Supabase-compatible JWT or RLS
 * policies (`auth.uid()` / `app_current_org_id()`) won't resolve.
 * `setSupabaseRealtimeAuth(token)` is called by the auth store after
 * login/hydrate; it's a no-op if the backend isn't configured to mint
 * the JWT, so the UX gracefully degrades to no-realtime.
 */
import { createClient } from "@supabase/supabase-js";

const url = process.env.REACT_APP_SUPABASE_URL || "";
const anon = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

export const supabase = (url && anon)
  ? createClient(url, anon, {
      auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
      realtime: { params: { eventsPerSecond: 5 } },
    })
  : null;

export const isRealtimeConfigured = () => !!supabase;

/**
 * Arm or disarm the Realtime client's JWT so RLS policies resolve.
 * Pass a non-empty token after login/hydrate; pass `null` on logout.
 * Silently no-ops if the Supabase client wasn't configured at boot.
 */
export const setSupabaseRealtimeAuth = (token) => {
  if (!supabase) return;
  try {
    supabase.realtime.setAuth(token || null);
  } catch { /* swallow — realtime stays calm */ }
};

